import type { Integration } from '@membranehq/sdk';
import { useCallback, useState } from 'react';
import {
  getFieldsFromAuthOptions,
  getFilteredFieldsForOption,
  getSchemaForIntegration,
} from 'src/utils/integrationUtils';

export const useIntegrationForm = (
  integration: Integration | null,
  selectedAuthOptionKey?: string,
) => {
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  const initializeFormData = useCallback(
    (integrationData: Integration | null) => {
      const initialData: Record<string, string> = {};
      const schema = getSchemaForIntegration(integrationData);

      if (schema?.properties) {
        Object.keys(schema.properties).forEach((key) => {
          initialData[key] = '';
        });
      } else {
        const authOptions = integrationData?.authOptions;
        if (authOptions && authOptions.length > 0) {
          const authFields = getFieldsFromAuthOptions(integrationData);
          authFields.forEach((key) => {
            initialData[key] = '';
          });
        } else if (schema && typeof schema === 'object') {
          Object.keys(schema).forEach((key) => {
            if (key !== 'required' && key !== 'type') {
              initialData[key] = '';
            }
          });
        }
      }

      setFormData(initialData);
      setErrors({});
    },
    [],
  );

  const handleInputChange = useCallback((key: string, value: string) => {
    setFormData((prev) => ({ ...prev, [key]: value }));

    setErrors((prev) => {
      if (prev[key]) {
        const newErrors = { ...prev };
        delete newErrors[key];
        return newErrors;
      }
      return prev;
    });
  }, []);

  const getSchema = useCallback(
    (integrationData?: Integration | null) => {
      const data = integrationData ?? integration;
      if (!data) return null;
      return getSchemaForIntegration(data);
    },
    [integration],
  );

  const validateForm = useCallback((): boolean => {
    const renderedFields = new Set(
      getFilteredFieldsForOption(integration, selectedAuthOptionKey),
    );
    const newErrors: Record<string, string> = {};

    const schema = getSchema();
    if (schema?.required) {
      schema.required.forEach((field: string) => {
        if (
          renderedFields.has(field) &&
          (!formData[field] || formData[field].trim() === '')
        ) {
          newErrors[field] = 'This field is required';
        }
      });
    }

    const authOptions = integration?.authOptions;
    if (authOptions) {
      authOptions.forEach((option) => {
        const optionSchema = option.ui?.schema;
        if (optionSchema?.required) {
          optionSchema.required.forEach((field: string) => {
            if (
              renderedFields.has(field) &&
              (!formData[field] || formData[field].trim() === '')
            ) {
              newErrors[field] = 'This field is required';
            }
          });
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, integration, selectedAuthOptionKey, getSchema]);

  const resetForm = useCallback(() => {
    setFormData({});
    setErrors({});
  }, []);

  return {
    formData,
    errors,
    setFormData,
    setErrors,
    initializeFormData,
    handleInputChange,
    getSchema,
    validateForm,
    resetForm,
  };
};
